/**
 * WizardUpload Component
 * Step 1: Upload files with drag & drop
 */
import React, { useCallback, useRef, useState } from 'react';

function WizardUpload({ 
  files, 
  onFilesAdded, 
  onFileRemove, 
  onNext,
  parsing,
  parseProgress,
  error 
}) {
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);
  
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files).filter(file => {
        const name = file.name.toLowerCase();
        return name.endsWith('.pdf') || name.endsWith('.txt') || name.endsWith('.md');
      });
      
      if (droppedFiles.length > 0) {
        onFilesAdded(droppedFiles);
      }
    }
  }, [onFilesAdded]);
  
  const handleFileSelect = useCallback((e) => {
    if (e.target.files && e.target.files.length > 0) {
      onFilesAdded(Array.from(e.target.files));
      // Reset input so same file can be selected again
      e.target.value = '';
    }
  }, [onFilesAdded]);
  
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  
  return (
    <div className="space-y-6">
      <div className="text-center mb-4">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
          Dateien hochladen
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Wähle PDF, TXT oder Markdown Dateien aus
        </p>
      </div>
      
      {/* Drop Zone */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`
          border-2 border-dashed rounded-xl p-8 text-center cursor-pointer
          transition-all duration-200
          ${dragActive
            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
            : 'border-gray-300 dark:border-gray-600 hover:border-primary-400 dark:hover:border-primary-500 hover:bg-gray-50 dark:hover:bg-gray-800/50'
          }
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.txt,.md"
          onChange={handleFileSelect}
          className="hidden"
        />
        
        <svg className="w-16 h-16 mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
        
        {parsing ? (
          <div className="space-y-2">
            <div className="text-gray-600 dark:text-gray-400">
              Verarbeite Dateien... ({parseProgress.current}/{parseProgress.total})
            </div>
            <div className="w-48 h-2 bg-gray-200 dark:bg-gray-700 rounded-full mx-auto overflow-hidden">
              <div 
                className="h-full bg-primary-500 rounded-full transition-all"
                style={{ width: `${(parseProgress.current / parseProgress.total) * 100}%` }}
              />
            </div>
          </div>
        ) : (
          <>
            <p className="text-gray-600 dark:text-gray-400 mb-2">
              {dragActive
                ? 'Dateien hier ablegen...'
                : 'Dateien hierher ziehen oder klicken zum Auswählen'
              }
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500">
              Unterstützt: PDF, TXT, Markdown
            </p>
          </>
        )}
      </div>
      
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg p-4 text-red-700 dark:text-red-300">
          <strong>Fehler:</strong> {error}
        </div>
      )}
      
      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-medium text-gray-800 dark:text-gray-200">
            Hochgeladene Dateien ({files.length})
          </h3>
          
          <div className="space-y-2">
            {files.map((file) => (
              <div
                key={file.id}
                className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  {file.name.endsWith('.pdf') ? (
                    <svg className="w-8 h-8 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" />
                      <path d="M14 2v6h6" />
                      <text x="50%" y="60%" fontSize="8" fill="white" textAnchor="middle" dominantBaseline="middle">PDF</text>
                    </svg>
                  ) : (
                    <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  )}
                  <div>
                    <p className="font-medium text-gray-800 dark:text-gray-200">
                      {file.name}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {formatFileSize(file.size)} • {file.totalPages} {file.totalPages === 1 ? 'Seite' : 'Seiten'}
                    </p>
                  </div>
                </div>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onFileRemove(file.id);
                  }}
                  className="p-2 text-gray-400 hover:text-red-500 dark:hover:text-red-400 theme-transition"
                  title="Datei entfernen"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
          
          {/* Summary */}
          <div className="text-sm text-gray-500 dark:text-gray-400 text-center">
            Gesamt: {files.reduce((sum, f) => sum + f.totalPages, 0)} Seiten
          </div>
        </div>
      )}
      
      {/* Navigation */}
      <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={onNext}
          disabled={files.length === 0 || parsing}
          className={`
            px-6 py-2.5 rounded-lg font-medium flex items-center gap-2 theme-transition
            ${files.length > 0 && !parsing
              ? 'bg-primary-500 hover:bg-primary-600 text-white'
              : 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
            }
          `}
        >
          Weiter
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export default WizardUpload;
